import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, Globe, FileText, CheckCircle } from 'lucide-react';
import { usePrivacyPrefs } from '../../contexts/PrivacyPrefsContext';
import {
  BROWSER_DISGUISES, FILENAME_PREFIXES,
  resolveBrowserDisguise, resolveFilenamePrefix,
  sanitizeCustomPrefix, isValidCustomPrefix,
  sanitizeCustomTabTitle, isValidCustomTabTitle,
} from '../../constants/privacyPresets';

/**
 * PrivacyPresetsCard — Browser Disguise + Discreet Filenames pickers,
 * side-by-side. Sits under the Code Name card on the "Your Privacy" tab.
 *
 * Both pickers show 5 curated presets + "Other — type your own" for
 * symmetry (founder request, Feb 2026). "Other" reveals a validated
 * custom text input; downloads and the browser-tab applier resolve the
 * effective value via `resolveFilenamePrefix` / `resolveBrowserDisguise`.
 */
const RadioRow = ({ testid, options, value, onChange, disabled }) => (
  <div className="space-y-2">
    {options.map((opt) => {
      const checked = value === opt.key;
      return (
        <label
          key={opt.key}
          data-testid={`${testid}-${opt.key}`}
          className={[
            'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
            checked
              ? 'border-emerald-400 bg-emerald-50/60'
              : 'border-slate-200 hover:border-slate-300 bg-white',
          ].join(' ')}
        >
          <input
            type="radio"
            className="accent-emerald-600"
            checked={checked}
            onChange={() => onChange(opt.key)}
            disabled={disabled}
          />
          <span className="text-sm font-medium text-slate-800">{opt.label}</span>
        </label>
      );
    })}
  </div>
);

const TabMockup = ({ prefs }) => {
  const preset = resolveBrowserDisguise(prefs);
  return (
    <div
      data-testid="browser-tab-preview"
      className="mb-4 flex items-center gap-2 max-w-[260px] rounded-t-lg rounded-b-sm bg-slate-100 border border-slate-200 border-b-slate-300 px-3 py-1.5 shadow-inner"
      aria-label="Preview of your browser tab"
    >
      {preset.favicon ? (
        <img src={preset.favicon} alt="" className="w-3.5 h-3.5 rounded-sm flex-shrink-0" />
      ) : (
        <div className="w-3.5 h-3.5 rounded-sm bg-emerald-600 flex-shrink-0" aria-hidden="true" />
      )}
      <span className="text-xs font-medium text-slate-700 truncate">{preset.tabTitle}</span>
      <span className="ml-auto text-slate-400 text-xs leading-none">×</span>
    </div>
  );
};

const FilenameExample = ({ prefs }) => {
  const today = new Date().toISOString().split('T')[0];
  const slug = resolveFilenamePrefix(prefs);
  return (
    <p className="mt-3 text-xs text-slate-500" data-testid="filename-live-example">
      Example: <span className="font-mono text-slate-700">{slug}-{today}.pdf</span>
    </p>
  );
};

/**
 * CustomInput — reused for both the custom filename prefix and the
 * custom browser-tab title. The parent supplies the sanitiser + validator
 * so the two flavours can enforce their different character rules.
 */
const CustomInput = ({
  value, onSave, disabled, testid,
  label, placeholder, hint, maxLength, sanitize, validate,
}) => {
  const [draft, setDraft] = useState(value || '');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (!dirty) setDraft(value || ''); }, [value, dirty]);

  const onChange = (e) => { setDirty(true); setDraft(sanitize(e.target.value)); };
  const valid = validate(draft);
  const changed = draft !== (value || '');
  const commit = async () => {
    if (!changed) return;
    if (!valid && draft !== '') return;
    await onSave(draft);
    setDirty(false);
  };

  return (
    <div
      className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200"
      data-testid={`${testid}-wrap`}
    >
      <label className="block text-xs font-medium text-slate-700 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <Input
          data-testid={testid}
          value={draft}
          onChange={onChange}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
          placeholder={placeholder}
          maxLength={maxLength}
          spellCheck={false}
          disabled={disabled}
          className="tracking-wide"
        />
        {valid && !changed && (
          <CheckCircle className="w-4 h-4 text-emerald-600" aria-label="Saved" />
        )}
      </div>
      <p className="mt-1.5 text-[11px] text-slate-500">{hint}</p>
    </div>
  );
};

const PrivacyPresetsCard = () => {
  const { prefs, loading, saving, updatePref } = usePrivacyPrefs();
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading privacy presets…</span>
      </div>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card data-testid="browser-disguise-card" className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" aria-hidden="true" />
            <span>Browser Disguise</span>
          </CardTitle>
          <CardDescription>
            Choose what the browser tab reads and which small icon shows. Doesn&apos;t change anything inside the app.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TabMockup prefs={prefs} />
          <RadioRow
            testid="browser-disguise"
            options={BROWSER_DISGUISES}
            value={prefs.browser_disguise}
            onChange={(v) => updatePref('browser_disguise', v)}
            disabled={saving}
          />
          {prefs.browser_disguise === 'custom' && (
            <CustomInput
              value={prefs.browser_disguise_custom}
              onSave={(v) => updatePref('browser_disguise_custom', v)}
              disabled={saving}
              testid="browser-disguise-custom-input"
              label="Your own tab title"
              placeholder="e.g. Weekend Reading"
              hint="3–24 characters. Letters, numbers, spaces, hyphens or apostrophes."
              maxLength={24}
              sanitize={sanitizeCustomTabTitle}
              validate={isValidCustomTabTitle}
            />
          )}
        </CardContent>
      </Card>

      <Card data-testid="filename-prefix-card" className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" aria-hidden="true" />
            <span>Downloaded File Names</span>
          </CardTitle>
          <CardDescription>
            Pick a discreet name for reports and exports. Files come out named this way.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioRow
            testid="filename-prefix"
            options={FILENAME_PREFIXES}
            value={prefs.filename_prefix}
            onChange={(v) => updatePref('filename_prefix', v)}
            disabled={saving}
          />
          {prefs.filename_prefix === 'custom' && (
            <CustomInput
              value={prefs.filename_prefix_custom}
              onSave={(v) => updatePref('filename_prefix_custom', v)}
              disabled={saving}
              testid="filename-custom-input"
              label="Your own name for downloaded files"
              placeholder="e.g. Weekend-Reading"
              hint="3–20 characters. Letters, numbers or hyphens. No spaces at the ends."
              maxLength={20}
              sanitize={sanitizeCustomPrefix}
              validate={isValidCustomPrefix}
            />
          )}
          <FilenameExample prefs={prefs} />
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyPresetsCard;
